import type {
  ArtifactStore,
  SnarkJSGroth16,
  BatchListUpdateEvent
} from '@railgun-community/wallet'
import { loadRailgunSdk, getLoadedRailgunSdk } from '@web/utils/railgunSdk'
import {
  MerkletreeScanUpdateEvent,
  NETWORK_CONFIG,
  NetworkName,
  POIProofProgressEvent,
  RailgunBalancesEvent,
  type POIList,
  POIProofEventStatus,
  TXIDVersion
} from '@railgun-community/shared-models'
import { Contract, JsonRpcProvider } from 'ethers'
import { emitRailgunEvent } from '@web/utils/railgunListener'
import { storage } from '@web/extension-services/background/webapi/storage'
import leveljs from 'level-js'
import { reportRailgunSecurityEvent } from './railgunSecurityTelemetry'
import { createBundledArtifactStore } from './railgunBundledArtifactStore'
import { createBrowserArtifactStore } from './railgunBrowserArtifactStore'
import { syncProfiler } from './railgunSyncProfiler'
import { networks } from '@ambire-common/consts/networks'
import type { RailgunTokenBalanceMap } from './railgunTokens'
import {
  PRIVATE_BALANCE_POPUP_SNAPSHOT_STORAGE_KEY,
  getPrivateBalanceSnapshotEntryKey,
  hasPositiveTokenAmount,
  type PrivateBalanceSnapshot
} from './railgunPrivateTransferPopupPreflight'

export const RAILGUN_DB_NAME_UI = 'zkx-railgun-db'
export const RAILGUN_ARTIFACT_DB_NAME_UI = 'zkx-railgun-artifacts'
export const RAILGUN_DB_NAME_BACKGROUND = 'zkx-railgun-db-sw'
export const RAILGUN_ARTIFACT_DB_NAME_BACKGROUND = 'zkx-railgun-artifacts-sw'
export const POLLING_INTERVAL_MS = 1000 * 60 * 5 // 5 min
export const RAILGUN_RAIL_OPERATION_TIMEOUT_MS = 2 * 60 * 1000
const WALLET_SOURCE = 'zkx wallet'

export const RAILGUN_DEBUG: boolean = true

// TEMPORARY scan-stall diagnostic. When true: starts the engine with shouldDebug +
// verboseScanLogging and forwards the SDK/engine internal logger to console
// (prefixed `[RailgunSDK]`). Used to locate the exact point a cold UTXO scan wedges
// (last getLogs block range / IDB write / error before the >180s idle freeze).
// REVERT to false once the wedge point is identified — engine debug is very verbose.
export const RAILGUN_SCAN_DIAG: boolean = false

// D2 "shared scan DB" design (railgun-spend-engine-warm-plan.md). When true: every
// scan worker + the main offscreen engine open ONE shared `RAILGUN_DB_NAME_BACKGROUND`
// instead of per-chain `-<chainId>` DBs. The per-chain worker scans UTXOs under its
// deterministic view-only wallet id; the small wallet-scoped namespace is relabeled
// onto the canonical (spend) wallet id (P2, `railgunRelabel.ts`) so the main engine's
// gas-estimate/proof sees the notes, and the main engine's RAM merkletree cache is
// cleared per-chain before estimate/proof (P3, `warmMainEngineForChain`) so it re-reads
// the tree the worker appended. ENABLED by default after P1–P3 passed: P1 proved
// level-js/IndexedDB tolerates 3 workers + main writing one DB with 0 corruption; P3
// verified real private sends on ARB+BSC. The merkletree (chain-scoped, large) is shared,
// never copied.
export const RAILGUN_SHARED_SCAN_DB: boolean = true

// Phase 2 (railgun-parallel-scan.md) — chains whose UTXO balance scan is routed
// to a dedicated view-only Web Worker (own engine + own per-chain IndexedDB) so
// its decrypt CPU runs off the offscreen main thread. The seam in
// `RailgunWallet.getBalance` delegates these chains to
// `refreshBalancesMaybeWorker`; any worker failure falls back to a main-thread
// scan, so this can only add parallelism, never lose a balance.
//
// Phase 3: all three private chains route to their own worker (ETH = 1, ARB =
// 42161, BSC = 56), each with its own engine + per-chain IndexedDB, so their
// decrypt CPU runs on three threads instead of serialized on one. Empty in
// production builds — the worker path ships disabled until validated.
//
// MEMORY (§7): workers partition merkletree/decrypted state PER chain, so the N
// per-chain engines sum to LESS peak than one monolithic engine holding all 3
// chains (measured 2026-05-27: old single-thread cold peak >4 GB vs new 3-worker
// ~2.5 GB). Workers spawn lazily (one per chain on first scan) and are torn down
// on STOP_ENGINE; stale view-only wallets are unloaded on account switch (Phase
// 4). Enabled in all envs — the worker path lowers OOM risk vs the old path.
export const RAILGUN_SCAN_WORKER_CHAIN_IDS: ReadonlySet<number> = new Set([1, 42161, 56])

// Variants to preload into IndexedDB via ArtifactDownloader.
export const PRELOAD_ARTIFACT_VARIANTS: readonly string[] = [
  'POI_3x3',
  'POI_13x13',
  '01x01',
  '01x02',
  '01x03',
  '02x01',
  '02x02',
  '03x01',
  '03x02',
  '04x01',
  '04x02',
  '05x01',
  '05x02'
]

export enum ExtendedPOIProofEventStatus {
  NewTransactionLoading = 'NewTransactionLoading',
  InProgress = POIProofEventStatus.InProgress,
  LoadingNextBatch = POIProofEventStatus.LoadingNextBatch,
  AllProofsCompleted = POIProofEventStatus.AllProofsCompleted,
  Error = POIProofEventStatus.Error
}

const RAILGUN_NETWORK_CHAIN_IDS: Record<NetworkName, number> = {
  [NetworkName.Ethereum]: 1,
  [NetworkName.Arbitrum]: 42161,
  [NetworkName.BNBChain]: 56,
  [NetworkName.Polygon]: 137,
  [NetworkName.Base]: 8453,
  [NetworkName.EthereumSepolia]: 11155111,
  [NetworkName.PolygonAmoy]: 80002,
  [NetworkName.Hardhat]: 31337,
  [NetworkName.EthereumRopsten_DEPRECATED]: 3,
  [NetworkName.EthereumGoerli_DEPRECATED]: 5,
  [NetworkName.ArbitrumGoerli_DEPRECATED]: 421613,
  [NetworkName.PolygonMumbai_DEPRECATED]: 80001
}

export function getRpcUrlForChainId(chainId: number): string {
  const network = networks.find((n) => n.chainId === BigInt(chainId))
  if (network?.selectedRpcUrl) return network.selectedRpcUrl
  const mainnetNetwork = networks.find((n) => n.chainId === 1n)
  return mainnetNetwork?.selectedRpcUrl ?? ''
}

type NetworkRpcConfig = {
  chainId: number
  providers: Array<{ provider: string; priority: number; weight: number; stallTimeout?: number }>
}

// Scan failover. The engine scans UTXO history with `queryFilter` wrapped in a hard 5s
// timeout (`EVENTS_SCAN_TIMEOUT`) and, on timeout, retries the SAME getLogs up to 30×
// (`MAX_SCAN_RETRIES`) with no backoff — see railgun-engine `scanAllEvents`. If the scan
// provider is a single endpoint and that endpoint slows under sustained cold-scan load,
// every retry hits the same slow endpoint, all 30 time out, the scan promise throws, and
// the cold scan dies silently (status stuck "Updated", never Complete). Observed: Invictus
// timing out getLogs on all three chains at the same instant (host-wide blip) wedged the
// whole sync (~180s idle → watchdog).
//
// Fix: give the scan an ethers `FallbackProvider` with an independent second RPC and a
// `stallTimeout` BELOW the engine's 5s budget. When the primary stalls past stallTimeout,
// ethers adds the failover; a healthy peer answers within 5s so `queryFilter` resolves and
// the scan keeps moving instead of cascading into the 30× timeout death.
const SCAN_STALL_TIMEOUT_MS = 2500

// Primary (Invictus) and failover get equal weight 2 → quorum = ceil(4/2) = 2, and ethers'
// `checkQuorum` accepts a single result whose weight ≥ quorum, so EITHER provider answering
// alone satisfies the request (no cross-provider agreement required). Primary has higher
// priority (lower number) so it's tried first; the failover only joins after stallTimeout.
const SCAN_PRIMARY_WEIGHT = 2
const SCAN_FAILOVER_WEIGHT = 2

// Pick an independent failover RPC for the cold scan from the chain's configured fallbacks.
// Prefer a publicnode endpoint (independent infra from Ambire's Invictus, proven responsive
// under the batched-499 getLogs pattern); else the first fallback distinct from the primary.
function getScanFailoverUrl(chainId: number, primaryUrl: string): string | undefined {
  const distinct = (RAILGUN_PER_CHAIN_CONFIG[chainId]?.fallbackRpcUrls ?? []).filter(
    (u) => u && u !== primaryUrl
  )
  return distinct.find((u) => u.includes('publicnode')) ?? distinct[0]
}

// Built lazily (not as a module-level const): the scan URL now routes through
// `getScanRpcUrlForChain`, which reads `RAILGUN_PER_CHAIN_CONFIG` — a const
// declared further down this file. Eager evaluation here would hit that const's
// temporal dead zone at import time. A function defers the read to call time.
export function getNetworkRpcConfig(networkName: NetworkName): NetworkRpcConfig {
  const chainId = RAILGUN_NETWORK_CHAIN_IDS[networkName]
  const primary = getScanRpcUrlForChain(chainId)
  if (!primary) return { chainId, providers: [] }
  const providers: NetworkRpcConfig['providers'] = [
    {
      provider: primary,
      priority: 1,
      weight: SCAN_PRIMARY_WEIGHT,
      stallTimeout: SCAN_STALL_TIMEOUT_MS
    }
  ]
  const failover = getScanFailoverUrl(chainId, primary)
  if (failover) {
    providers.push({
      provider: failover,
      priority: 2,
      weight: SCAN_FAILOVER_WEIGHT,
      stallTimeout: SCAN_STALL_TIMEOUT_MS
    })
  }
  return { chainId, providers }
}

const BROWSER_USE_NATIVE_ARTIFACTS = false

/**
 * Downloads RAILGUN proof artifacts into the given store (IndexedDB).
 * Swallows errors so engine init / callers are not failed by network issues.
 */
export async function preloadRailgunArtifacts(artifactStore: ArtifactStore): Promise<void> {
  const { ArtifactDownloader } = await loadRailgunSdk()
  const downloader = new ArtifactDownloader(artifactStore, BROWSER_USE_NATIVE_ARTIFACTS)
  try {
    for (const variant of PRELOAD_ARTIFACT_VARIANTS) {
      // eslint-disable-next-line no-await-in-loop
      await downloader.downloadArtifacts(variant)
      if (RAILGUN_DEBUG) {
        // eslint-disable-next-line no-console
        console.log(`[Railgun artifacts] preloaded variant: ${variant}`)
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[Railgun artifacts] preload failed:', error)
  }
}

export type InitializeRailgunEngineOptions = {
  /** When true, blocks until artifact preload finishes (slower first open, warmer cache for private UI). */
  awaitArtifactPreload?: boolean
  /** Level/IndexedDB for merkletrees; default UI DB. Background should pass `RAILGUN_DB_NAME_BACKGROUND`. */
  merkletreeDbName?: string
  /** Level/IndexedDB for proof artifacts; default UI DB. Background should pass `RAILGUN_ARTIFACT_DB_NAME_BACKGROUND`. */
  artifactDbName?: string
}

const onBalanceUpdateCallback = (balancesEvent: RailgunBalancesEvent) => {
  // eslint-disable-next-line no-console
  console.log('[Railgun balance] update', {
    walletID: balancesEvent.railgunWalletID?.slice(0, 10),
    chain: balancesEvent.chain?.id,
    bucket: balancesEvent.balanceBucket,
    tokens: balancesEvent.erc20Amounts?.length
  })
  emitRailgunEvent('balanceUpdate', balancesEvent)
}

const onPOIProofProgressCallback = (poiProofProgressEvent: POIProofProgressEvent) => {
  if (RAILGUN_DEBUG) {
    // eslint-disable-next-line no-console
    console.log('onPOIProofProgressCallback', poiProofProgressEvent)
  }
  emitRailgunEvent('poiProofProgress', poiProofProgressEvent)
}

const onUTXOMerkletreeScanCallback = (utxoMerkletreeScanEvent: MerkletreeScanUpdateEvent) => {
  syncProfiler.utxoScan(utxoMerkletreeScanEvent)
  if (RAILGUN_DEBUG) {
    // eslint-disable-next-line no-console
    console.log('onUTXOMerkletreeScanCallback', utxoMerkletreeScanEvent)
  }
  emitRailgunEvent('utxoScanUpdate', utxoMerkletreeScanEvent)
}

const onTXIDMerkletreeScanCallback = (txidMerkletreeScanEvent: MerkletreeScanUpdateEvent) => {
  syncProfiler.txidScan(txidMerkletreeScanEvent)
  if (RAILGUN_DEBUG) {
    // eslint-disable-next-line no-console
    console.log('onTXIDMerkletreeScanCallback', txidMerkletreeScanEvent)
  }
  emitRailgunEvent('txidScanUpdate', txidMerkletreeScanEvent)
}

const onBatchListCallback = (batchListEvent: BatchListUpdateEvent) => {
  syncProfiler.poiBatch(batchListEvent as unknown as { chain?: { id: number } })
  if (RAILGUN_DEBUG) {
    // eslint-disable-next-line no-console
    console.log('onBatchListCallback', batchListEvent)
  }
  emitRailgunEvent('batchListUpdate', batchListEvent)
}

export async function initializeRailgunEngine(
  options?: InitializeRailgunEngineOptions
): Promise<void> {
  const {
    setLoggers,
    startRailgunEngine,
    setOnBalanceUpdateCallback,
    setOnWalletPOIProofProgressCallback,
    setOnUTXOMerkletreeScanCallback,
    setOnTXIDMerkletreeScanCallback,
    setBatchListCallback,
    getProver
  } = await loadRailgunSdk()
  const merkletreeDbName = options?.merkletreeDbName ?? RAILGUN_DB_NAME_UI
  const artifactDbName = options?.artifactDbName ?? RAILGUN_ARTIFACT_DB_NAME_UI
  const db = leveljs(merkletreeDbName)
  // Chrome MV3 forbids remote *code* (JS / WASM not in the package). WASM
  // circuits and verification keys are bundled inside the extension; zkey
  // proving keys (non-executable data, ~600 MB total) are fetched lazily by
  // the SDK from its public IPFS gateway and SHA-256-verified. The hybrid
  // `createBundledArtifactStore` serves bundled wasm/vkey and stores zkey in
  // IndexedDB. The legacy IndexedDB-only store is kept for non-extension
  // contexts (tests).
  const artifactStore =
    typeof chrome !== 'undefined' && chrome.runtime && typeof chrome.runtime.getURL === 'function'
      ? await createBundledArtifactStore()
      : await createBrowserArtifactStore(artifactDbName)

  const shouldDebug = RAILGUN_SCAN_DIAG
  const useNativeArtifacts = false
  const skipMerkletreeScans = false
  const poiNodeURLs = getPoiNodeUrlsForEngine()
  const customPOILists: POIList[] = []
  const verboseScanLogging = RAILGUN_SCAN_DIAG

  try {
    if (RAILGUN_SCAN_DIAG) {
      // Forward the engine/SDK internal logger to the offscreen console so a stall
      // trace shows the last scan action before the freeze. Set before any scan runs.
      setLoggers(
        (msg: string) => {
          syncProfiler.sdkLog(msg)
          // eslint-disable-next-line no-console
          console.log('[RailgunSDK]', msg)
        },
        (err: Error | string) => {
          // eslint-disable-next-line no-console
          console.error('[RailgunSDK]', err)
        }
      )
    }

    await startRailgunEngine(
      WALLET_SOURCE,
      db,
      shouldDebug,
      artifactStore,
      useNativeArtifacts,
      skipMerkletreeScans,
      poiNodeURLs,
      customPOILists,
      verboseScanLogging
    )

    installReorgMarginPatch()

    setOnBalanceUpdateCallback(onBalanceUpdateCallback)
    setOnWalletPOIProofProgressCallback(onPOIProofProgressCallback)
    setOnUTXOMerkletreeScanCallback(onUTXOMerkletreeScanCallback)
    setOnTXIDMerkletreeScanCallback(onTXIDMerkletreeScanCallback)
    setBatchListCallback(onBatchListCallback)

    const canUseSnarkJsProver =
      typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function'
    if (canUseSnarkJsProver) {
      const { groth16 } = await import('snarkjs')
      getProver().setSnarkJSGroth16(groth16 as unknown as SnarkJSGroth16)
      if (RAILGUN_DEBUG) {
        // eslint-disable-next-line no-console
        console.log('[Railgun] SnarkJS groth16 prover initialized')
      }
    } else {
      // eslint-disable-next-line no-console
      console.warn('[Railgun] Skipping SnarkJS prover init: URL.createObjectURL is unavailable')
    }

    // disabled for now, as it's not needed for the extension
    // const preloadPromise = preloadRailgunArtifacts(artifactStore)
    // if (options?.awaitArtifactPreload) {
    //   await preloadPromise
    // } else {
    //   void preloadPromise
    // }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to initialize Railgun engine:', error)
    throw error
  }
}

export const NETWORKS_FOR_BALANCE: NetworkName[] = [
  NetworkName.Ethereum,
  NetworkName.Arbitrum,
  NetworkName.BNBChain
  // NetworkName.Polygon — disabled for now
]

const CHAIN_ID_TO_NETWORK_NAME: Record<number, NetworkName> = Object.fromEntries(
  NETWORKS_FOR_BALANCE.map((name) => [NETWORK_CONFIG[name].chain.id, name])
) as Record<number, NetworkName>

export function getNetworkNameForChainId(chainId: number): NetworkName | undefined {
  return CHAIN_ID_TO_NETWORK_NAME[chainId]
}

/**
 * Phase 3.4 — typed per-chain config object that consolidates the knobs we tune
 * separately per chain. Today this folds in:
 *   - `reorgMargin` (from Phase 0.3): blocks below head we stop scanning at, to
 *     avoid ingesting commitments that get reorged out.
 *   - `rpcOverrideUrl`: optional explicit RPC URL for this chain. When unset
 *     (default) we fall through to `getRpcUrlForChainId`, which reads from the
 *     user's Ambire network settings. Useful for pinning a known-good provider
 *     for the merkleRoot anchor when the user's default RPC is rate-limited.
 *   - `poiNodeUrls`: optional per-chain POI aggregator override. Today the SDK
 *     accepts a single global list at `startRailgunEngine`; this field is
 *     wired into engine boot via `getPoiNodeUrlsForEngine()` (dedup union of
 *     every chain's list). Per-chain *routing* (chain X queries node A only)
 *     would need an SDK change — tracked as a carry-forward.
 *
 * Single source of truth: every per-chain switch in this file should read from
 * here rather than its own constant map.
 */
export type RailgunPerChainConfig = {
  reorgMargin: number
  rpcOverrideUrl?: string
  /**
   * Phase 5.1 — optional RPC URL for the UTXO merkletree cold scan, independent from
   * `rpcOverrideUrl` and the verify/quorum primary. Currently UNSET for every chain (scan uses
   * the user's `getRpcUrlForChainId`, i.e. Invictus). Kept as a hook, with a deliberate caution:
   *
   * An earlier revision pinned publicnode/arb1 here to cut request count, on the assumption that
   * the scan's cost scales with the provider's `eth_getLogs` block-range cap (Invictus = 5k). That
   * is FALSE — the engine scans in fixed `SCAN_CHUNKS = 499`-block getLogs (batched ~100 per HTTP
   * request via ethers, with a 5s SDK timeout per getLogs), so the provider cap is irrelevant to
   * the scan. Under that real batched pattern Invictus and publicnode both respond in 1–3s. The
   * override bought nothing and added a third-party that flaked on the SDK's single init
   * `getBlockNumber` probe (leaving the chain provider-less for the session). If you set this
   * again, pick a provider proven reliable under the batched-499 pattern + the init probe, not one
   * with a high cap. It does NOT touch the quorum/anchor path (`getRpcUrlForRailgunChain` /
   * `getQuorumRpcUrlsForChain` keep Invictus as the verify primary).
   */
  scanRpcUrl?: string
  poiNodeUrls?: string[]
  /**
   * Phase 4.1 — public fallback RPC URLs used as the "second vote" in the
   * merkleRoot/nullifier quorum. Independent from the user's configured RPC so
   * a malicious primary can be detected. Default to well-known public
   * endpoints; advanced users can override per-chain. Soft quorum: if all of
   * these are unreachable, the verification falls back to single-RPC and the
   * result is flagged `confidence: 'low'` rather than failing.
   */
  fallbackRpcUrls?: string[]
}

export const RAILGUN_PER_CHAIN_CONFIG: Record<number, RailgunPerChainConfig> = {
  1: {
    reorgMargin: 12,
    // NOTE: an earlier revision pinned `scanRpcUrl: publicnode` here to "speed up" the cold scan
    // on the theory that Invictus's 5k eth_getLogs cap forced too many requests. That theory was
    // wrong: the engine scans in fixed `SCAN_CHUNKS = 499`-block getLogs (batched ~100/HTTP via
    // ethers, 5s SDK timeout each), so the provider's max block-range cap never bottlenecks the
    // scan. Under the real batched-499 pattern both Invictus and publicnode respond in 1–3s, but
    // publicnode is a third party that flaked on the SDK's single `getBlockNumber` init probe
    // (leaving Ethereum provider-less) and Invictus (Ambire's dedicated infra) reliably completed
    // chains in testing. So scan stays on the user's Invictus RPC (default). See `scanRpcUrl` doc.
    // Replaced the previous set (cloudflare-eth = rate-limited, eth.llamarpc = HTTP 525,
    // rpc.ankr.com/eth = now requires an API key → "Unauthorized") with public endpoints
    // verified reachable 2026-05. These feed the soft-quorum merkleRoot verification only.
    fallbackRpcUrls: [
      'https://ethereum-rpc.publicnode.com',
      'https://eth.drpc.org',
      'https://1rpc.io/eth'
    ]
  },
  42161: {
    reorgMargin: 60,
    // scanRpcUrl override removed — see the chain-1 note. The scan uses the user's Invictus RPC.
    // rpc.ankr.com/arbitrum now requires an API key; swapped for publicnode.
    fallbackRpcUrls: ['https://arb1.arbitrum.io/rpc', 'https://arbitrum-one-rpc.publicnode.com']
  },
  56: {
    reorgMargin: 15,
    // rpc.ankr.com/bsc now requires an API key; swapped for publicnode.
    fallbackRpcUrls: ['https://bsc-dataseed.binance.org', 'https://bsc-rpc.publicnode.com']
  },
  137: {
    reorgMargin: 64,
    // rpc.ankr.com/polygon now requires an API key; polygon-bor-rpc.publicnode.com
    // is flagged as phishing by Norton (2026-11-03), so drpc instead.
    fallbackRpcUrls: ['https://polygon-rpc.com', 'https://polygon.drpc.org']
  }
}

const DEFAULT_POI_NODE_URLS = ['https://ppoi.fdi.network']

/**
 * Phase 3.4 — union of every chain's POI node URL list, with the global
 * default as fallback. The engine takes a single global list, so per-chain
 * routing is not yet a real feature; this getter exists so the day we add
 * routing it has one consumer to update.
 */
export function getPoiNodeUrlsForEngine(): string[] {
  const set = new Set<string>(DEFAULT_POI_NODE_URLS)
  for (const cfg of Object.values(RAILGUN_PER_CHAIN_CONFIG)) {
    for (const url of cfg.poiNodeUrls ?? []) set.add(url)
  }
  return Array.from(set)
}

/** Phase 3.4 — typed accessor for the per-chain config. Returns undefined for unconfigured chains. */
export function getRailgunPerChainConfig(chainId: number): RailgunPerChainConfig | undefined {
  return RAILGUN_PER_CHAIN_CONFIG[chainId]
}

/**
 * Phase 3.4 — RPC URL with override. Per-chain `rpcOverrideUrl` wins; otherwise
 * fall back to the user's Ambire network settings via `getRpcUrlForChainId`.
 * The merkleRoot anchor and any future direct RPC calls should route through
 * this so a single pin in `RAILGUN_PER_CHAIN_CONFIG` is enough to swap providers.
 */
export function getRpcUrlForRailgunChain(chainId: number): string {
  const override = RAILGUN_PER_CHAIN_CONFIG[chainId]?.rpcOverrideUrl
  if (override) return override
  return getRpcUrlForChainId(chainId)
}

/**
 * Phase 5.1 — RPC URL for the UTXO merkletree cold scan. Per-chain `scanRpcUrl`
 * wins; otherwise fall through to the user's selected RPC. Distinct from
 * `getRpcUrlForRailgunChain` (the verify/quorum primary) on purpose: we want the
 * scan on a high-`eth_getLogs`-cap provider while merkleRoot/nullifier checks
 * stay on the user's trusted RPC. See `RailgunPerChainConfig.scanRpcUrl`.
 */
export function getScanRpcUrlForChain(chainId: number): string {
  const scanUrl = RAILGUN_PER_CHAIN_CONFIG[chainId]?.scanRpcUrl
  if (scanUrl) return scanUrl
  return getRpcUrlForChainId(chainId)
}

/**
 * Phase 4.1 — list of RPC URLs to use for quorum verification.
 *
 * Returns the primary URL first, then any per-chain fallbacks, deduped and
 * with empties dropped. Caller takes the first 2 (or only 1 if that's all
 * that's available — soft quorum). The primary is whatever
 * `getRpcUrlForRailgunChain` returns (usually the user's Ambire setting,
 * possibly overridden by `rpcOverrideUrl`).
 */
export function getQuorumRpcUrlsForChain(chainId: number): string[] {
  const seen = new Set<string>()
  const out: string[] = []
  const add = (url?: string) => {
    if (!url) return
    if (seen.has(url)) return
    seen.add(url)
    out.push(url)
  }
  add(getRpcUrlForRailgunChain(chainId))
  for (const url of RAILGUN_PER_CHAIN_CONFIG[chainId]?.fallbackRpcUrls ?? []) add(url)
  return out
}

let reorgMarginPatchInstalled = false
const reorgMarginLoggedChains = new Set<number>()

/**
 * Install a one-time wrap of `engine.loadNetwork` so that whenever the engine loads
 * a provider for a chain, we replace `defaultProvider.getBlockNumber` (per-instance)
 * with one that subtracts the chain's reorg margin. Engine call site:
 * `railgun-engine.js` line ~392, `railgunSmartWalletContract.contract.runner.provider.getBlockNumber()`.
 *
 * We patch per-instance (not on FallbackProvider.prototype) because the engine bundles
 * its own nested copy of `ethers`, so its FallbackProvider class is a different runtime
 * constructor than the one in the host app's `ethers` import.
 */
export function installReorgMarginPatch(): void {
  if (reorgMarginPatchInstalled) return
  reorgMarginPatchInstalled = true
  // eslint-disable-next-line no-console
  console.log('[Railgun] reorg-margin patch installed', RAILGUN_PER_CHAIN_CONFIG)

  const { getEngine } = getLoadedRailgunSdk()
  const engine = getEngine() as any
  if (!engine || typeof engine.loadNetwork !== 'function') {
    // eslint-disable-next-line no-console
    console.warn('[Railgun] reorg-margin patch: engine.loadNetwork unavailable; skipping')
    return
  }
  const originalLoadNetwork = engine.loadNetwork.bind(engine)
  engine.loadNetwork = async function patchedLoadNetwork(
    chain: { type: number; id: number },
    ...rest: any[]
  ) {
    // defaultProvider is the 7th arg per engine signature; it's the FallbackProvider.
    const defaultProvider = rest[5]
    const margin = RAILGUN_PER_CHAIN_CONFIG[chain?.id]?.reorgMargin
    if (
      defaultProvider &&
      margin &&
      margin > 0 &&
      typeof defaultProvider.getBlockNumber === 'function'
    ) {
      const originalGetBlockNumber = defaultProvider.getBlockNumber.bind(defaultProvider)
      defaultProvider.getBlockNumber = async function patchedGetBlockNumber() {
        const head = await originalGetBlockNumber()
        const capped = Math.max(head - margin, 0)
        if (!reorgMarginLoggedChains.has(chain.id)) {
          reorgMarginLoggedChains.add(chain.id)
          // eslint-disable-next-line no-console
          console.log(
            `[Railgun] reorg-margin active chain=${chain.id} head=${head} -> capped=${capped} (margin=${margin})`
          )
        }
        return capped
      }
    }
    return originalLoadNetwork(chain, ...rest)
  }
}

export type MerkleRootCheckResult = {
  networkName: NetworkName
  chainId: number
  treeNumber: number | null
  localRoot: string | null
  // null = could not check (no engine, no RPC, no merkletree); true = on-chain confirms; false = mismatch.
  valid: true | false | null
  // Phase 4.1 — quorum confidence:
  //   'high'  — at least 2 RPCs agreed (typical case with one fallback reachable)
  //   'low'   — only one RPC was reachable and it confirmed the root; safe to display
  //             but a malicious primary cannot be detected. UI may want to badge this.
  //   'none'  — no RPC succeeded; valid=null and error explains why
  confidence?: 'high' | 'low' | 'none'
  // Phase 4.1 — number of RPCs that successfully responded for this check
  rpcCount?: number
  error?: string
}

const RAILGUN_PROXY_ABI = [
  'function rootHistory(uint256 tree, bytes32 root) view returns (bool)',
  // Phase 4.2 — emitted by RailgunSmartWallet on every spend. We can't query
  // a contract getter (no public nullifiers mapping), so we scan a recent
  // block window for these events to detect missed nullifications.
  'event Nullified(uint16 treeNumber, bytes32[] nullifier)'
]

/**
 * Phase 0.2 of RAILGUN_HYBRID_REWRITE_PLAN.md — cryptographic anchor.
 *
 * For each network, read the local UTXO merkletree's latest tree+root from the SDK engine
 * and verify it against the on-chain RailgunSmartWallet proxy via `rootHistory(tree, root)`.
 * Returns one entry per network. Caller decides what to do on mismatch (we set
 * `cachePoisoned` on the balance summary and avoid the freshness short-circuit on retry).
 *
 * V2 only — V3 chains use a different accumulator contract not yet wired here. Skipped
 * networks return `valid: null` with an `error` reason.
 */
export async function verifyMerkleRootsForNetworks(
  networkNames: ReadonlyArray<NetworkName>
): Promise<MerkleRootCheckResult[]> {
  const { getEngine } = await loadRailgunSdk()
  let engine: ReturnType<typeof getEngine> | null = null
  try {
    engine = getEngine()
  } catch (err) {
    return networkNames.map((networkName) => ({
      networkName,
      chainId: NETWORK_CONFIG[networkName]?.chain.id ?? -1,
      treeNumber: null,
      localRoot: null,
      valid: null,
      error: 'engine not initialized'
    }))
  }

  return Promise.all(
    networkNames.map(async (networkName): Promise<MerkleRootCheckResult> => {
      const network = NETWORK_CONFIG[networkName]
      const chainId = network?.chain.id ?? -1
      const proxyAddr = network?.proxyContract
      const base: MerkleRootCheckResult = {
        networkName,
        chainId,
        treeNumber: null,
        localRoot: null,
        valid: null
      }
      if (!network || !proxyAddr) return { ...base, error: 'no proxy contract address' }

      let treeNumber: number
      let localRoot: string
      try {
        const merkletree = engine!.getUTXOMerkletree(TXIDVersion.V2_PoseidonMerkle, network.chain)
        treeNumber = await merkletree.latestTree()
        localRoot = await merkletree.getRoot(treeNumber)
      } catch (err: any) {
        return { ...base, error: `local merkletree read failed: ${err?.message || err}` }
      }
      if (!localRoot) return { ...base, treeNumber, error: 'local root empty' }

      const rpcUrls = getQuorumRpcUrlsForChain(chainId).slice(0, 2)
      if (rpcUrls.length === 0) {
        return { ...base, treeNumber, localRoot, confidence: 'none', error: 'no rpc url' }
      }
      const rootHex = localRoot.startsWith('0x') ? localRoot : `0x${localRoot}`
      const padded = rootHex.length === 66 ? rootHex : `0x${rootHex.slice(2).padStart(64, '0')}`

      // Phase 4.1 — soft-quorum: query both RPCs in parallel, tolerate
      // single-RPC failure. Both succeed + agree → high confidence; both
      // succeed + disagree → mismatch (cache poisoned); only one succeeds →
      // low confidence (single source of truth, can't detect a malicious
      // primary but UI is still usable).
      const queryRpc = async (url: string): Promise<boolean | Error> => {
        try {
          const provider = new JsonRpcProvider(url)
          const contract = new Contract(proxyAddr, RAILGUN_PROXY_ABI, provider)
          return (await (contract as any).rootHistory(treeNumber, padded)) as boolean
        } catch (err: any) {
          return err instanceof Error ? err : new Error(String(err))
        }
      }
      const settled = await Promise.all(rpcUrls.map(queryRpc))
      const successes = settled.filter((r): r is boolean => typeof r === 'boolean')
      const errors = settled.filter((r): r is Error => r instanceof Error)
      const rpcCount = successes.length

      if (successes.length === 0) {
        return {
          ...base,
          treeNumber,
          localRoot: padded,
          confidence: 'none',
          rpcCount: 0,
          error: `all rpcs failed: ${errors.map((e) => e.message).join('; ')}`
        }
      }

      // 2-RPC disagreement: at least one says invalid. Treat as cache poisoned —
      // a malicious primary cannot mask a fallback's negative answer.
      const allAgree = successes.every((v) => v === successes[0])
      if (!allAgree) {
        // Phase 4.5 — disagreement between two independent RPCs on a contract
        // read is the strongest possible signal of a malicious primary. Page
        // immediately even though the downstream code already flips
        // cachePoisoned.
        reportRailgunSecurityEvent('merkleRootQuorumDisagreement', {
          networkName,
          chainId,
          treeNumber,
          localRoot: padded,
          rpcCount,
          results: successes.join(',')
        })
        return {
          networkName,
          chainId,
          treeNumber,
          localRoot: padded,
          valid: false,
          confidence: 'high',
          rpcCount,
          error: `rpc disagreement: results=[${successes.join(', ')}]`
        }
      }

      return {
        networkName,
        chainId,
        treeNumber,
        localRoot: padded,
        valid: successes[0] ?? null,
        confidence: rpcCount >= 2 ? 'high' : 'low',
        rpcCount
      }
    })
  )
}

/**
 * Phase 4.2 of RAILGUN_HYBRID_REWRITE_PLAN.md — on-chain nullifier verifier.
 *
 * Verifies whether each of a caller-supplied set of nullifiers has appeared on
 * chain in a recent block window. The intended use is a pre-spend check: the
 * unshield/transfer flow can pass in the nullifiers of the notes it's about to
 * spend; if any come back `present: true`, the local cache is stale (the SDK
 * missed an event) and the spend MUST be refused, not retried with a poisoned
 * tree.
 *
 * Implementation note: the RailgunSmartWallet contract does NOT expose a public
 * nullifier mapping — only `Nullified(uint16, bytes32[])` events. We therefore
 * scan `eth_getLogs` over the last `lookbackBlocks` blocks and check membership.
 * Defaults to 50_000 blocks (≈1 week on Ethereum, ≈3 days on Arbitrum) which
 * covers any plausible "missed nullification" gap; callers with a known
 * lastSyncedBlock should pass that as `fromBlock` for a tighter window.
 *
 * Quorum: same soft-quorum as Phase 4.1 — query both RPCs in parallel, both
 * agree → `confidence: 'high'`, only one reachable → `confidence: 'low'`,
 * disagreement → flagged in the result so callers can refuse the spend.
 */
export type NullifierStatusResult = {
  chainId: number
  /** map of input nullifier (lowercased 0x hex) → true if found on-chain in window */
  presence: Record<string, boolean>
  /** highest block scanned */
  toBlock: number | null
  /** lowest block scanned */
  fromBlock: number | null
  confidence: 'high' | 'low' | 'none'
  rpcCount: number
  /** quorum disagreed: at least one RPC saw a nullifier the other didn't */
  quorumDisagreement?: boolean
  error?: string
}

export type VerifyNullifiersOptions = {
  /** explicit start of scan range (inclusive) */
  fromBlock?: number
  /** explicit end of scan range (inclusive); defaults to latest */
  toBlock?: number
  /** when fromBlock is unset, scan this many blocks back from head */
  lookbackBlocks?: number
}

export async function verifyNullifierStatusOnChain(
  chainId: number,
  nullifiers: ReadonlyArray<string>,
  opts: VerifyNullifiersOptions = {}
): Promise<NullifierStatusResult> {
  const networkName = CHAIN_ID_TO_NETWORK_NAME[chainId]
  const network = networkName ? NETWORK_CONFIG[networkName] : undefined
  const proxyAddr = network?.proxyContract
  const base: NullifierStatusResult = {
    chainId,
    presence: Object.fromEntries(nullifiers.map((n) => [n.toLowerCase(), false])),
    toBlock: null,
    fromBlock: null,
    confidence: 'none',
    rpcCount: 0
  }
  if (!proxyAddr) return { ...base, error: 'no proxy contract address' }
  if (nullifiers.length === 0) return { ...base, confidence: 'high' }

  const wanted = new Set(
    nullifiers.map((n) => (n.startsWith('0x') ? n.toLowerCase() : `0x${n.toLowerCase()}`))
  )

  const rpcUrls = getQuorumRpcUrlsForChain(chainId).slice(0, 2)
  if (rpcUrls.length === 0) return { ...base, error: 'no rpc url' }

  const lookback = opts.lookbackBlocks ?? 50_000
  const queryRpc = async (
    url: string
  ): Promise<{ found: Set<string>; from: number; to: number } | Error> => {
    try {
      const provider = new JsonRpcProvider(url)
      const head = opts.toBlock ?? (await provider.getBlockNumber())
      const fromBlock = opts.fromBlock ?? Math.max(head - lookback, 0)
      const contract = new Contract(proxyAddr, RAILGUN_PROXY_ABI, provider)
      const filter = contract.filters.Nullified!()
      // ethers v6 returns EventLog[]; .args[1] is the nullifier bytes32[] payload.
      const events = await contract.queryFilter(filter, fromBlock, head)
      const found = new Set<string>()
      for (const ev of events) {
        const args = (ev as any).args
        const list: ReadonlyArray<string> | undefined = args?.[1] ?? args?.nullifier
        if (!list) continue
        for (const n of list) {
          const lower = String(n).toLowerCase()
          if (wanted.has(lower)) found.add(lower)
        }
      }
      return { found, from: fromBlock, to: head }
    } catch (err: any) {
      return err instanceof Error ? err : new Error(String(err))
    }
  }
  const settled = await Promise.all(rpcUrls.map(queryRpc))
  const successes = settled.filter(
    (r): r is { found: Set<string>; from: number; to: number } => !(r instanceof Error)
  )
  const errors = settled.filter((r): r is Error => r instanceof Error)
  if (successes.length === 0) {
    return {
      ...base,
      confidence: 'none',
      error: `all rpcs failed: ${errors.map((e) => e.message).join('; ')}`
    }
  }

  const presence: Record<string, boolean> = {}
  for (const n of wanted) presence[n] = false
  const union = new Set<string>()
  for (const s of successes) for (const f of s.found) union.add(f)
  for (const n of union) presence[n] = true

  // disagreement: any RPC's set differs from the union
  let disagreement = false
  if (successes.length >= 2) {
    const sizes = successes.map((s) => s.found.size)
    if (sizes.some((sz) => sz !== union.size)) disagreement = true
  }
  if (disagreement) {
    // Phase 4.5 — RPCs disagreed on which nullifiers exist on chain. Caller
    // code must refuse the spend; the alert tells us this is happening.
    reportRailgunSecurityEvent('nullifierQuorumDisagreement', {
      chainId,
      fromBlock: successes[0]!.from,
      toBlock: successes[0]!.to,
      rpcCount: successes.length,
      foundCount: union.size
    })
  }
  // Any nullifier marked present on-chain when the caller asked about it
  // means the local cache is stale (the caller treats those as spendable).
  // Leave the spend-refusal logic to the caller; here we just emit telemetry
  // when at least one match was found in the on-chain scan.
  if (union.size > 0) {
    reportRailgunSecurityEvent('nullifierMismatch', {
      chainId,
      foundCount: union.size,
      fromBlock: successes[0]!.from,
      toBlock: successes[0]!.to
    })
  }

  return {
    chainId,
    presence,
    fromBlock: successes[0]!.from,
    toBlock: successes[0]!.to,
    confidence: successes.length >= 2 ? 'high' : 'low',
    rpcCount: successes.length,
    ...(disagreement ? { quorumDisagreement: true } : {})
  }
}

export function getNetworksForBalance(chainId: number | bigint | string | null): NetworkName[] {
  if (chainId == null) return NETWORKS_FOR_BALANCE
  const id = typeof chainId === 'bigint' ? Number(chainId) : Number(chainId)
  const name = CHAIN_ID_TO_NETWORK_NAME[id]
  return name != null ? [name] : NETWORKS_FOR_BALANCE
}

export async function loadRailgunProviders(): Promise<void> {
  const { loadProvider } = await loadRailgunSdk()
  await Promise.allSettled(
    NETWORKS_FOR_BALANCE.map(async (networkName) => {
      const config = getNetworkRpcConfig(networkName)
      const scanPrimary = config.providers[0]?.provider
      if (!scanPrimary) return
      const scanFailover = config.providers[1]?.provider
      // Surface the resolved SCAN providers per chain so a sync trace shows the failover
      // topology actually in effect at runtime. Always-on (RAILGUN_DEBUG is off in prod).
      // eslint-disable-next-line no-console
      console.log('[Railgun loadProvider] scan provider', {
        networkName,
        chainId: config.chainId,
        scanPrimary,
        scanFailover: scanFailover ?? '(none)',
        verifyPrimary: getRpcUrlForRailgunChain(config.chainId)
      })
      // The SDK's loadNetwork validates the provider with a single `getBlockNumber()` and throws
      // (no retry) if it fails — leaving the chain with NO provider for the whole engine session.
      // The failover FallbackProvider makes this robust: the init probe passes as long as EITHER
      // peer answers (Invictus alone satisfies quorum). Steps:
      //   1. load the full failover config (Invictus primary + independent failover peer),
      //   2. retry once (covers a transient init blip),
      //   3. last resort — Invictus alone, so the chain still scans (no failover) rather than
      //      silently dying. Weight 2 keeps `createFallbackProviderFromJsonConfig` happy (needs
      //      total weight ≥ 2).
      const tryLoad = async (cfg: NetworkRpcConfig, label: string): Promise<boolean> => {
        try {
          await loadProvider(cfg, networkName, POLLING_INTERVAL_MS)
          return true
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn(`[Railgun loadProvider] ${label} failed for ${networkName}:`, err)
          return false
        }
      }
      if (await tryLoad(config, 'scan')) return
      if (await tryLoad(config, 'scan-retry')) return
      const soloUrl = getRpcUrlForChainId(config.chainId)
      // eslint-disable-next-line no-console
      console.warn(
        `[Railgun loadProvider] failover config unavailable for ${networkName}; loading ${soloUrl} alone (no scan failover)`
      )
      await tryLoad(
        { chainId: config.chainId, providers: [{ provider: soloUrl, priority: 1, weight: 2 }] },
        'fallback-solo'
      )
    })
  )
}

export async function stopRailgunEngineIfRunning(): Promise<void> {
  try {
    const { stopRailgunEngine: sdkStopRailgunEngine } = await loadRailgunSdk()
    await sdkStopRailgunEngine()
  } catch (err) {
    if (RAILGUN_DEBUG) {
      // eslint-disable-next-line no-console
      console.warn('[Railgun] stop engine skipped/failed:', err)
    }
  }
}

export async function persistPrivateBalanceSnapshot(params: {
  storageKey?: string
  accountAddr: string
  balanceLastUpdatedAt: number
  balanceTokenMap: RailgunTokenBalanceMap
  missingInternalPOIBalanceTokenMap: RailgunTokenBalanceMap
  missingExternalPOIBalanceTokenMap: RailgunTokenBalanceMap
}): Promise<void> {
  const {
    storageKey = PRIVATE_BALANCE_POPUP_SNAPSHOT_STORAGE_KEY,
    accountAddr,
    balanceLastUpdatedAt,
    balanceTokenMap,
    missingInternalPOIBalanceTokenMap,
    missingExternalPOIBalanceTokenMap
  } = params

  const existingSnapshot = (await storage.get(storageKey, {})) as PrivateBalanceSnapshot
  const nextSnapshot: PrivateBalanceSnapshot = { ...existingSnapshot }
  const knownChains = new Set<number>([
    ...Array.from(balanceTokenMap.keys()),
    ...Array.from(missingInternalPOIBalanceTokenMap.keys()),
    ...Array.from(missingExternalPOIBalanceTokenMap.keys())
  ])

  for (const chainId of knownChains) {
    const chainTokens = new Set<string>()
    ;(balanceTokenMap.get(chainId) ?? []).forEach((item) =>
      chainTokens.add(item.tokenAddress.toLowerCase())
    )
    ;(missingInternalPOIBalanceTokenMap.get(chainId) ?? []).forEach((item) =>
      chainTokens.add(item.tokenAddress.toLowerCase())
    )
    ;(missingExternalPOIBalanceTokenMap.get(chainId) ?? []).forEach((item) =>
      chainTokens.add(item.tokenAddress.toLowerCase())
    )

    for (const tokenAddress of chainTokens) {
      const hasSpendableBalance = hasPositiveTokenAmount(balanceTokenMap, chainId, tokenAddress)
      const hasMissingPOI =
        hasPositiveTokenAmount(missingInternalPOIBalanceTokenMap, chainId, tokenAddress) ||
        hasPositiveTokenAmount(missingExternalPOIBalanceTokenMap, chainId, tokenAddress)

      const key = getPrivateBalanceSnapshotEntryKey({
        accountAddr,
        chainId,
        tokenAddress
      })
      nextSnapshot[key] = {
        hasSpendableBalance,
        hasMissingPOI,
        updatedAt: balanceLastUpdatedAt
      }
    }
  }

  await storage.set(storageKey, nextSnapshot)
}
